import React, { FormEvent } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogTitle, Stack } from '@mui/material';

import { useCreateCommission } from '~/hooks/apis/agent/useCreateCommission';
import { useUpdateCommission } from '~/hooks/apis/agent/useUpdateCommission';
import useUserBreadcrumbs from '~/hooks/useUserBreadcrumbs';

import type { Commission, EditCommissionParams } from '~/types/services/agentHub';

import { TEXT_TRANSFORM_NONE } from './CommissionDialog.types';
import CommissionForm from './CommissionForm';
import { buildCommissionPayload } from './commissionFormPayload';
import { useCommissionForm } from './useCommissionForm';

interface Props {
  data?: Commission;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}

export default function CommissionDialog({ data, onClose, isOpen, mode }: Props) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const { enqueueSnackbar } = useSnackbar();
  const {
    value,
    resetForm,
    isLoadingConditions,
    addCondition,
    handleChangeCondition,
    handleConditionOperatorChange,
    handleClearCondition,
    handleAppendCondition,
    onChange,
  } = useCommissionForm({ data, isOpen });

  const createCommissionMutation = useCreateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule created', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });
  const updateCommissionMutation = useUpdateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule updated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  const { data: creator, isLoading: isLoadingCreator } = useUserBreadcrumbs({
    userId: isEdit ? (data?.createdBy ?? null) : null,
    enabled: Boolean(isEdit && data?.createdBy),
  });
  const { data: updater, isLoading: isLoadingUpdater } = useUserBreadcrumbs({
    userId: isEdit ? (data?.updatedBy ?? null) : null,
    enabled: Boolean(isEdit && data?.updatedBy),
  });

  function onCloseModal() {
    resetForm();
    onClose();
  }

  function handleSaveCommission(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const result = buildCommissionPayload(value, isEdit ? data : undefined);
    if ('error' in result) {
      enqueueSnackbar(result.error, { variant: 'error' });
      return;
    }

    const onSuccess = () => {
      const hasChangedLimit =
        (data?.limit && Number(value.limit) !== data?.limit) || (!data?.limit && Number(value.limit) !== 0);
      if (hasChangedLimit) {
        enqueueSnackbar('Limit updated, this may take a few minutes to take effect', { variant: 'warning' });
      }
      resetForm();
      onClose();
    };

    if (isCreate) {
      createCommissionMutation.mutate(result.payload, { onSuccess });
      return;
    }

    updateCommissionMutation.mutate(
      { commissionId: data?.id, data: result.payload as EditCommissionParams },
      { onSuccess },
    );
  }

  const isSaving = createCommissionMutation.isPending || updateCommissionMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onCloseModal}>
      <DialogTitle>{isCreate ? 'Create commission rule' : `Edit commission rule "${data?.description}"`}</DialogTitle>
      <form onSubmit={handleSaveCommission}>
        <Stack padding={2} paddingTop={0} direction="column" spacing={2}>
          <CommissionForm
            value={value}
            onChange={onChange}
            data={data}
            isLoadingConditions={isLoadingConditions}
            creator={creator ?? undefined}
            updater={updater ?? undefined}
            isLoadingCommissionEditHistory={isLoadingCreator || isLoadingUpdater}
            addCondition={addCondition}
            handleChangeCondition={handleChangeCondition}
            handleConditionOperatorChange={handleConditionOperatorChange}
            handleClearCondition={handleClearCondition}
            handleAppendCondition={handleAppendCondition}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button sx={TEXT_TRANSFORM_NONE} type="button" size="medium" variant="text" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button sx={TEXT_TRANSFORM_NONE} type="submit" size="medium" variant="contained" disabled={isSaving}>
              {isSaving ? 'Saving...' : isCreate ? 'Create rule' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  );
}
